/**
 * Aspect ratios as defined by Figma
 */
export enum AspectRatio {
  CLASSIC_HOR = '↔ 3:2',
  WIDE_HOR = '↔ 16:9',
  STANDARD_HOR = '↔︎ 2:1',
  FEATURE_HOR = '↔ 5:2',
  PANO_HOR = '↔ 3:1',
  SQUARE = '↔ 1:1',
  CLASSIC_VER = '↕ 2:3',
  WIDE_VER = '↕ 9:16',
  STANDARD_VER = '↕︎ 1:2',
  FEATURE_VER = '↕ 2:5',
  PANO_VER = '↕ 1:3',
}

/**
 * Smart crop types as defined by Dynamic Media
 */
export enum SmartCrop {
  CLASSIC_HOR = 'Classic-Hor',
  WIDE_HOR = 'Wide-Hor',
  STANDARD_HOR = 'Standard-Hor',
  FEATURE_HOR = 'Feature-Hor',
  PANO_HOR = 'Pano-Hor',
  SQUARE = 'Square',
  CLASSIC_VER = 'Classic-Ver',
  WIDE_VER = 'Wide-Ver',
  STANDARD_VER = 'Standard-Ver',
  FEATURE_VER = 'Feature-Ver',
  PANO_VER = 'Pano-Ver',
}

/**
 * Ratios as numbers for height = width / ratio calculations
 */
export enum SmartCropRatio {
  CLASSIC_HOR = 1.5,
  WIDE_HOR = 1.8,
  STANDARD_HOR = 2,
  FEATURE_HOR = 2.5,
  PANO_HOR = 3,
  SQUARE = 1,
  CLASSIC_VER = 0.7,
  WIDE_VER = 0.56,
  STANDARD_VER = 0.5,
  FEATURE_VER = 0.4,
  PANO_VER = 0.33,
}

/**
 * Map to get the SmartCrop type from the AspectRatio
 */
export const SmartCropFromAspectRatio = new Map<AspectRatio, SmartCrop | void>([
  [AspectRatio.CLASSIC_HOR, SmartCrop.CLASSIC_HOR],
  [AspectRatio.WIDE_HOR, SmartCrop.WIDE_HOR],
  [AspectRatio.STANDARD_HOR, SmartCrop.STANDARD_HOR],
  [AspectRatio.FEATURE_HOR, SmartCrop.FEATURE_HOR],
  [AspectRatio.PANO_HOR, SmartCrop.PANO_HOR],
  [AspectRatio.SQUARE, SmartCrop.SQUARE],
  [AspectRatio.CLASSIC_VER, SmartCrop.CLASSIC_VER],
  [AspectRatio.WIDE_VER, SmartCrop.WIDE_VER],
  [AspectRatio.STANDARD_VER, SmartCrop.STANDARD_VER],
  [AspectRatio.FEATURE_VER, SmartCrop.FEATURE_VER],
  [AspectRatio.PANO_VER, SmartCrop.PANO_VER],
]);

/**
 * Map to get the height ratio from the AspectRatio
 */
export const HeightRatioFromAspectRatio = new Map<AspectRatio, SmartCropRatio | number>([
  [AspectRatio.CLASSIC_HOR, SmartCropRatio.CLASSIC_HOR],
  [AspectRatio.WIDE_HOR, SmartCropRatio.WIDE_HOR],
  [AspectRatio.STANDARD_HOR, SmartCropRatio.STANDARD_HOR],
  [AspectRatio.FEATURE_HOR, SmartCropRatio.FEATURE_HOR],
  [AspectRatio.PANO_HOR, SmartCropRatio.PANO_HOR],
  [AspectRatio.SQUARE, SmartCropRatio.SQUARE],
  [AspectRatio.CLASSIC_VER, SmartCropRatio.CLASSIC_VER],
  [AspectRatio.WIDE_VER, SmartCropRatio.WIDE_VER],
  [AspectRatio.STANDARD_VER, SmartCropRatio.STANDARD_VER],
  [AspectRatio.FEATURE_VER, SmartCropRatio.FEATURE_VER],
  [AspectRatio.PANO_VER, SmartCropRatio.PANO_VER],
]);

/**
 * Viewport breakpoints in pixels, for reference
 */
export enum ViewportPx {
  XS = 0 /* 0-575 */,
  SM = 576 /* 576-767 */,
  MD = 768 /* 768-991 */,
  LG = 992 /* 992-1199 */,
  XL = 1200 /* 1200+ */,
  // XXL = 1920, /* TODO: Make the 1920 breakpoint available if needed */
}

/**
 * Acceptable media query values by breakpoint
 */
export enum MediaQuery {
  // MIN_WIDTH_XXL = `(min-width: 1920px)`, /* TODO: Make the 1920 breakpoint available if needed */
  MIN_WIDTH_XL = `(min-width: 1200px)`,
  MIN_WIDTH_LG = `(min-width: 992px)`,
  MIN_WIDTH_MD = `(min-width: 768px)`,
  MIN_WIDTH_SM = `(min-width: 576px)`,
  MIN_WIDTH_XS = `(min-width: 0px)`,
  // MAX_WIDTH_XL = `(max-width: 1919px)`, /* TODO: Make the 1920 breakpoint available if needed */
  MAX_WIDTH_LG = `(max-width: 1199px)`,
  MAX_WIDTH_MD = `(max-width: 991px)`,
  MAX_WIDTH_SM = `(max-width: 767px)`,
  MAX_WIDTH_XS = `(max-width: 575px)`,
}

export const MediaQueryPictureOrder = [
  // MediaQuery.MIN_WIDTH_XXL, /* TODO: Make the 1920 breakpoint available if needed */
  MediaQuery.MIN_WIDTH_XL,
  MediaQuery.MIN_WIDTH_LG,
  MediaQuery.MIN_WIDTH_MD,
  MediaQuery.MIN_WIDTH_SM,
  MediaQuery.MIN_WIDTH_XS,
  // MediaQuery.MAX_WIDTH_XL, /* TODO: Make the 1920 breakpoint available if needed */
  MediaQuery.MAX_WIDTH_LG,
  MediaQuery.MAX_WIDTH_MD,
  MediaQuery.MAX_WIDTH_SM,
  MediaQuery.MAX_WIDTH_XS,
];

export const MediaQueryStyleOrder = [
  MediaQuery.MIN_WIDTH_XS,
  MediaQuery.MAX_WIDTH_XS,
  MediaQuery.MIN_WIDTH_SM,
  MediaQuery.MAX_WIDTH_SM,
  MediaQuery.MIN_WIDTH_MD,
  MediaQuery.MAX_WIDTH_MD,
  MediaQuery.MIN_WIDTH_LG,
  MediaQuery.MAX_WIDTH_LG,
  MediaQuery.MIN_WIDTH_XL,
  // MediaQuery.MAX_WIDTH_XL, /* TODO: Make the 1920 breakpoint available if needed */
  // MediaQuery.MIN_WIDTH_XXL, /* TODO: Make the 1920 breakpoint available if needed */
];

/**
 * Image renditions specification for generating a list of image renditions
 */
export interface ResponsiveImageSpec {
  media: MediaQuery;
  aspectRatio: AspectRatio;
  imgWidthPx: number;
  imgHeightPx?: number;
}

/**
 * Image rendition definition (inspired by AEM model.json) to be provided in a
 * list to the AImage atom
 */
export interface ImageRendition {
  renditionPath: string; // the full srcset path
  media: MediaQuery;
  aspectRatio?: AspectRatio;
  smartCropType?: SmartCrop | null;
  dynamic?: boolean;
}

/**
 * Returns a string suitable for use in a picture source element or img srcset,
 * using the provided asset path and image specifications.
 *
 * @example
 * // returns "https://example.com/image.jpg:Classic-Hor?wid=767&fit=constrain, https://example.com/image.jpg:Classic-Hor?wid=1534&fit=constrain 2x"
 * buildDynamicRenditionPath('https://example.com/image.jpg', AspectRatio.CLASSIC_HOR, 767)
 */
export function buildDynamicRenditionPath(
  assetPath: string,
  aspectRatio?: AspectRatio,
  widthPx = 1200,
  fit = 'constrain'
): string {
  const smartCrop = aspectRatio ? SmartCropFromAspectRatio.get(aspectRatio) : undefined;
  const heightPx = smartCrop
    ? undefined
    : Math.round(
        widthPx / ((aspectRatio && HeightRatioFromAspectRatio.get(aspectRatio)) || SmartCropRatio.CLASSIC_HOR)
      );
  const path1 = `${assetPath}${smartCrop ? `:${smartCrop}` : ''}?wid=${widthPx}${
    heightPx ? `&hei=${heightPx}` : ''
  }&fit=${fit}`;
  const path2 = `${assetPath}${smartCrop ? `:${smartCrop}` : ''}?wid=${widthPx * 2}${
    heightPx ? `&hei=${heightPx * 2}` : ''
  }&fit=${fit} 2x`;
  return [path1, path2].join(', ');
}

/**
 * Converts a list of ImageRenditionSpec items to a list of ImageRendition
 * objects (inspired by AEM model.json)
 */
export function generateImageRenditions(renditionSpecs: ResponsiveImageSpec[] = [], assetPath = ''): ImageRendition[] {
  return renditionSpecs.map(({ media, aspectRatio, imgWidthPx }) => ({
    media,
    aspectRatio,
    smartCropType: SmartCropFromAspectRatio.get(aspectRatio) ?? null,
    renditionPath: buildDynamicRenditionPath(assetPath, aspectRatio, imgWidthPx),
  }));
}

/**
 * Sorts the given array of ImageRenditions according to the provided sort
 * order of MediaQueries. Returns a new array of ImageRenditions without
 * mutating the original.
 */
export function sortRenditions(renditions: ImageRendition[], sortOrder: MediaQuery[]) {
  return [...renditions].sort((a, b) => {
    try {
      return sortOrder.indexOf(a.media as MediaQuery) - sortOrder.indexOf(b.media as MediaQuery);
    } catch (err) {
      return 0;
    }
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isMediaQuery(value: any): value is MediaQuery {
  return Object.values(MediaQuery).includes(value as MediaQuery);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAspectRatio(value: any): value is AspectRatio {
  return Object.values(AspectRatio).includes(value as AspectRatio);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isSmartCrop(value: any): value is SmartCrop {
  return Object.values(SmartCrop).includes(value as SmartCrop);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isImageRendition(value: any): value is ImageRendition {
  return (
    value &&
    typeof value === 'object' &&
    (value.renditionPath === undefined || typeof value.renditionPath === 'string') &&
    (value.media === undefined || isMediaQuery(value.media)) &&
    (value.aspectRatio === undefined || isAspectRatio(value.aspectRatio)) &&
    (value.smartCropType === undefined || value.smartCropType === null || isSmartCrop(value.smartCropType)) &&
    (value.dynamic === undefined || typeof value.dynamic === 'boolean')
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isImageRenditionSpec(value: any): value is ResponsiveImageSpec {
  return (
    value &&
    typeof value === 'object' &&
    isMediaQuery((value as ResponsiveImageSpec).media) &&
    isAspectRatio((value as ResponsiveImageSpec).aspectRatio) &&
    typeof value.imgWidthPx === 'number' &&
    value.imgWidthPx >= 0
  );
}
